import { Button } from '@/atoms/Button'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { ExportTrayIcon } from '@/atoms/Icons/ExportTrayIcon'
import { ParagraphSM, TitleXS } from '@/atoms/Text'
import { ReactFCC } from '@/types/react'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { UploadErrorMessage, UploadErrorType } from './UploadErrorMessage'

interface UploadInactiveStateProps {
  acknowledged: boolean
  isActive: boolean
  error?: UploadErrorType
  onDismissError: () => void
}

export const UploadInactiveState: ReactFCC<UploadInactiveStateProps> = ({
  acknowledged,
  isActive,
  error,
  onDismissError,
}) => {
  const { t } = useTranslate('guild-create')

  if (!isActive) {
    return (
      <div className="flex select-none flex-col items-center gap-1">
        <CircleInfoIcon size={48} color="info-500" />
        <ParagraphSM>{t('missingRequiredInputs', 'Missing required inputs.')}</ParagraphSM>
      </div>
    )
  }

  if (!acknowledged) {
    return (
      <div className="flex select-none flex-col items-center gap-1">
        <CircleInfoIcon size={48} color="info-500" />
        <ParagraphSM>
          {t('toUploadPleaseAcknowledgeRestrictions', 'To upload, please acknowledge the content restrictions.')}
        </ParagraphSM>
      </div>
    )
  }

  if (error) {
    return <UploadErrorMessage error={error} dismiss={onDismissError} />
  }

  return null
}

interface FileTypeHintProps {
  acceptedFileType: 'image' | 'video' | 'text'
  type?: 'APPLICATION' | 'UPDATE' | 'PROJECT'
  maxSizeText: string
}

export const FileTypeHint: ReactFCC<FileTypeHintProps> = ({ acceptedFileType, type, maxSizeText }) => {
  const { t } = useTranslate('guild-create')

  if (acceptedFileType === 'video') {
    return (
      <ParagraphSM className="text-center text-xs" color="core-gray-800">
        {type === 'UPDATE' ? '16:9 or 9:16' : '16:9 or 21:9'} {t('preferred', 'Preferred.')} m4a, mpg, mp4, mov.{' '}
        {maxSizeText}
      </ParagraphSM>
    )
  }
  if (acceptedFileType === 'image') {
    return (
      <ParagraphSM className="text-xs" color="core-gray-800">
        Landscape. JPG, PNG, or HEIC. {maxSizeText}
      </ParagraphSM>
    )
  }
  if (acceptedFileType === 'text') {
    return (
      <ParagraphSM className="text-xs" color="core-gray-800">
        Subtitles. vtt, srt, scc. {maxSizeText}
      </ParagraphSM>
    )
  }
  return null
}

interface UploadPromptProps {
  boxTitle?: string
  acceptedFileType: 'image' | 'video' | 'text'
  type?: 'APPLICATION' | 'UPDATE' | 'PROJECT'
  maxSizeText: string
  onBrowseClick: () => void
}

export const UploadPrompt: ReactFCC<UploadPromptProps> = ({
  boxTitle,
  acceptedFileType,
  type,
  maxSizeText,
  onBrowseClick,
}) => {
  const { t } = useTranslate('guild-create')

  return (
    <div className="flex select-none flex-col items-center gap-2">
      <ExportTrayIcon size={24} />
      {boxTitle && <TitleXS>{boxTitle}</TitleXS>}
      <ParagraphSM weight="bold">
        <Translate t={t} i18nKey="dragAndDropOr" defaults="Drag and drop or" />
        <span className="border-oxide font-bold text-oxide hover:border-b-2" onClick={onBrowseClick}>
          &nbsp;{t('browse', 'Browse')}
        </span>
      </ParagraphSM>
      <FileTypeHint acceptedFileType={acceptedFileType} type={type} maxSizeText={maxSizeText} />
    </div>
  )
}

interface UploadProgressProps {
  uploadProgress: number
  isPreparingUpload: boolean
  file: File | null
  onAbort: () => void
}

export const UploadProgress: ReactFCC<UploadProgressProps> = ({ uploadProgress, isPreparingUpload, file, onAbort }) => {
  const { t } = useTranslate('guild-create')

  return (
    <div className="flex select-none flex-col items-center gap-1">
      <ParagraphSM className="flex justify-between">
        {isPreparingUpload ? t('preparingUpload', 'Preparing upload...') : t('uploading', 'Uploading...')}
        <span className="w-10 pl-1 text-right">{uploadProgress}%</span>
      </ParagraphSM>

      {isPreparingUpload && (
        <ParagraphSM color="core-gray-600" className="text-center text-xs">
          {t('gettingUploadPermissions', 'Getting upload permissions from server...')}
        </ParagraphSM>
      )}

      {!isPreparingUpload && (
        <div className="flex w-full max-w-xs flex-col gap-1">
          <div className="w-full">
            <div className="h-2 w-full overflow-hidden rounded bg-core-gray-200">
              <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        </div>
      )}

      <ParagraphSM color="core-gray-600" className="py-2 text-center">
        {t('thisMayTakeAFewMinutesDontLeave', "This may take a few minutes. Don't leave the page.")}
      </ParagraphSM>
      {file && (
        <Button variant="gray" onClick={onAbort}>
          {t('cancel', 'Cancel')}
        </Button>
      )}
    </div>
  )
}

export const UploadComplete: ReactFCC = () => {
  const { t } = useTranslate('guild-create')

  return (
    <div className="flex select-none flex-col items-center gap-1">
      <div className="bg-green-100 flex h-12 w-12 items-center justify-center rounded-full">
        <CheckIcon size={24} color="success-600" />
      </div>
      <ParagraphSM weight="bold" className="text-green-700">
        {t('uploadComplete', 'Upload Complete')}
      </ParagraphSM>
      <ParagraphSM color="core-gray-600" className="text-center text-xs">
        {t('uploadCompleteMessage', 'Your file has been uploaded successfully.')}
      </ParagraphSM>
    </div>
  )
}
